import { isNonEmptyString, isPositiveOrZero, reduceErrors, validate, ValidationErrors } from "@util/validations"
import { totalForCoveredAdjustments } from "@models/payments/Adjustment"

export interface NonRefundableAdjustment {
  nonRefundableAddonId?: number
  nonRefundableDateId?: number
  nonRefundableInsuranceStatusId?: number
  nonRefundable: boolean
  transactionType: "credit" | "debit"
  participationId: number
  coveredByInsurance?: boolean
  description: string
  price: number
}

export function isForInsurance(adjustment): boolean {
  return adjustment.nonRefundableInsuranceStatusId !== undefined
}

export function insuranceIsNonRefundable(nonRefundableAdjustments: NonRefundableAdjustment[]): boolean {
  let totalNonRefundable = nonRefundableAdjustments.reduce((acc, a) => acc + a.price, 0)
  let totalForAddons = totalForNonRefundableAddons(nonRefundableAdjustments)
  let totalForInsurance = totalForNonRefundableInsurance(nonRefundableAdjustments)
  return (
    totalNonRefundable > totalForAddons + totalForInsurance ||
    totalForNonRefundableInsuredAddons(nonRefundableAdjustments) > 0
  )
}

export function totalClaimable(adjustments: NonRefundableAdjustment[]): number {
  return (
    totalForNonRefundableDates(adjustments) +
    totalForNonRefundableInsuredAddons(adjustments) +
    totalForCoveredAdjustments(adjustments)
  )
}

export function totalForNonRefundableDates(adjustments: NonRefundableAdjustment[]): number {
  return adjustments.filter((a) => a.nonRefundableDateId !== undefined).reduce((acc, a) => acc + a.price, 0)
}

export function totalForNonRefundableInsuredAddons(adjustments: NonRefundableAdjustment[]): number {
  return adjustments
    .filter((a) => a.nonRefundableAddonId !== undefined && a.coveredByInsurance)
    .reduce((acc, a) => acc + a.price, 0)
}

export function totalForNonRefundableAddons(adjustments: NonRefundableAdjustment[]): number {
  return adjustments.filter((a) => a.nonRefundableAddonId !== undefined).reduce((acc, a) => acc + a.price, 0)
}

export function totalForNonRefundableInsurance(adjustments: NonRefundableAdjustment[]): number {
  return adjustments.filter((a) => a.nonRefundableInsuranceStatusId !== undefined).reduce((acc, a) => acc + a.price, 0)
}

export function currentNonRefundableInsurance(adjustments: NonRefundableAdjustment[], insuranceOnly: boolean): number {
  return insuranceOnly || insuranceIsNonRefundable(adjustments) ? totalForNonRefundableInsurance(adjustments) : 0
}

export function totalNonRefundable(nonRefundableAdjustments: NonRefundableAdjustment[], insuranceOnly: boolean): number {
  let totalNonRefundable = nonRefundableAdjustments.reduce((acc, a) => acc + a.price, 0)

  return (
    totalNonRefundable -
    totalForNonRefundableInsurance(nonRefundableAdjustments) +
    currentNonRefundableInsurance(nonRefundableAdjustments, insuranceOnly)
  )
}

export function validateAdjustment(data: NonRefundableAdjustment): ValidationErrors<NonRefundableAdjustment> {
  return reduceErrors({
    price: [validate(isPositiveOrZero(data.price), "Amount must be zero or more")],
    description: [validate(isNonEmptyString(data.description), "Description must not be empty")],
  })
}

export function maybeExcludingInsurance(
  adjustments: NonRefundableAdjustment[],
  insuranceOnly: boolean,
): NonRefundableAdjustment[] {
  // Insurance gets refunded when there's nothing left to claim, so we filter that non-refundable out
  // But for insurance only trips we don't know if money was actually paid or not, so we keep the non-refundable in there.
  if (totalClaimable(adjustments) === 0 && insuranceOnly === false) {
    return adjustments.filter((a) => a.nonRefundable && a.nonRefundableInsuranceStatusId === undefined)
  } else {
    return adjustments
  }
}
